import { useState, type ReactNode } from 'react';
import { Box, Button, Card, Chip, Container, Grid, Stack, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import HandshakeRounded from '@mui/icons-material/HandshakeRounded';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import ShieldRounded from '@mui/icons-material/ShieldRounded';
import TravelExploreRounded from '@mui/icons-material/TravelExploreRounded';
import VerifiedRounded from '@mui/icons-material/VerifiedRounded';
import { Link as RouterLink } from 'react-router';
import { DEFAULT_BRAND, type BrandState } from '../brands';
import { BrandLab } from './BrandLab';
import { AntiSpamDemo, DuoDemo, FreeVsPaid, GooglePreview, MealAiDemo } from './FeatureDemos';

function FeatureTile({
  icon,
  eyebrow,
  title,
  text,
  children,
}: {
  icon: ReactNode;
  eyebrow?: string;
  title: string;
  text: ReactNode;
  children?: ReactNode;
}) {
  return (
    <Card sx={{ height: '100%', p: { xs: 2.5, md: 3 } }}>
      <Stack spacing={2} sx={{ height: '100%' }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            aria-hidden
            sx={(theme) => ({
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: theme.vars.palette.primary.container,
              color: theme.vars.palette.primary.onContainer,
            })}
          >
            {icon}
          </Box>
          {eyebrow && <Chip size="small" variant="soft" color="accent" label={eyebrow} />}
        </Stack>
        <Box>
          <Typography variant="h6" component="h3">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            {text}
          </Typography>
        </Box>
        {children && <Box sx={{ mt: 'auto' }}>{children}</Box>}
      </Stack>
    </Card>
  );
}

/**
 * The pitch to professionals: what they get here that generic apps don't give.
 * Everything shown is a working piece of the product (or labeled as example).
 */
export function ForTrainersSection({ id = 'para-personais' }: { id?: string }) {
  const [brand, setBrand] = useState<BrandState>(DEFAULT_BRAND);

  return (
    <Box
      id={id}
      component="section"
      aria-labelledby={`${id}-title`}
      sx={(theme) => ({
        py: { xs: 8, md: 12 },
        background: `linear-gradient(180deg, ${theme.vars.palette.background.default}, ${theme.alpha(theme.vars.palette.primary.container, 0.55)} 30%, ${theme.vars.palette.background.default})`,
        scrollMarginTop: 72,
      })}
    >
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ textAlign: 'center', alignItems: 'center', mb: { xs: 5, md: 7 } }}>
          <Chip variant="soft" color="primary" label="Para personal trainers" />
          <Typography id={`${id}-title`} variant="h2" component="h2" sx={{ maxWidth: 760 }}>
            Seu perfil aqui é grátis. E ele é só o começo.
          </Typography>
          <Typography
            variant="h6"
            component="p"
            color="text.secondary"
            sx={{ maxWidth: 680, fontWeight: 400 }}
          >
            Aparecer aqui e receber pedidos de alunos é grátis. Quando quiser ir além, assine a
            plataforma e tenha um app com a sua marca: treinos, nutrição, evolução e IA num lugar
            só.
          </Typography>
        </Stack>

        <Card
          sx={(theme) => ({
            p: { xs: 2.5, sm: 4, md: 5 },
            mb: 3,
            borderRadius: `${Number(theme.shape.borderRadius) * 2.4}px`,
            boxShadow: `0 40px 100px -60px ${theme.alpha(theme.vars.palette.primary.dark, 0.6)}`,
          })}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', mb: 3 }}>
            <PaletteRounded color="primary" />
            <Typography variant="h5" component="h3">
              Sua marca, não a nossa. Experimente:
            </Typography>
          </Stack>
          <BrandLab value={brand} onChange={setBrand} />
        </Card>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FeatureTile
              icon={<GroupsRounded />}
              eyebrow="Exclusivo"
              title="Personal e nutricionista no mesmo aluno"
              text="Monte uma dupla com um nutricionista: cada um cuida da sua parte e o aluno decide o que cada um pode ver. Os apps genéricos não fazem isso."
            >
              <DuoDemo />
            </FeatureTile>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FeatureTile
              icon={<AutoAwesomeRounded />}
              eyebrow="IA"
              title="O aluno fotografa o prato, a IA anota"
              text="Chega de planilha de dieta: a IA reconhece os alimentos da foto e estima as calorias. O aluno confere e salva."
            >
              <MealAiDemo />
            </FeatureTile>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FeatureTile
              icon={<TravelExploreRounded />}
              title="Apareça no Google"
              text="Cada perfil tem uma página própria, pronta para as buscas da sua cidade. Veja como a sua apareceria:"
            >
              <GooglePreview name={brand.name} color={brand.color} />
            </FeatureTile>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FeatureTile
              icon={<ShieldRounded />}
              title="Pedidos de gente real, sem spam"
              text="Quem pede contato entra com Google e tem no máximo 5 pedidos em aberto. Você aceita só quem quiser."
            >
              <AntiSpamDemo />
            </FeatureTile>
          </Grid>
          <Grid size={12}>
            <FeatureTile
              icon={<HandshakeRounded />}
              title="O que é grátis e o que é pago"
              text="Sem letras miúdas: o perfil é grátis para sempre; o app com a sua marca é a assinatura."
            >
              <FreeVsPaid />
            </FeatureTile>
          </Grid>
        </Grid>

        <Card
          sx={(theme) => ({
            mt: 3,
            p: { xs: 3, md: 4 },
            color: theme.vars.palette.primary.contrastText,
            border: 'none',
            background: `linear-gradient(120deg, ${theme.vars.palette.primary.dark}, ${theme.vars.palette.primary.main} 60%, ${theme.vars.palette.primary.light})`,
          })}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={3}
            sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
          >
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <VerifiedRounded sx={{ fontSize: 40, opacity: 0.9 }} />
              <Box>
                <Typography variant="h5" component="p">
                  Crie seu perfil grátis em 5 minutos
                </Typography>
                <Typography sx={{ opacity: 0.85 }}>
                  Informe seu CREF: nossa equipe confere e seu perfil ganha o selo de verificado.
                </Typography>
              </Box>
            </Stack>
            <Button
              component={RouterLink}
              to="/painel"
              size="large"
              variant="contained"
              color="accent"
              endIcon={<ArrowForwardRounded />}
              sx={{ flexShrink: 0 }}
            >
              Criar meu perfil grátis
            </Button>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
}
